import React, { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useInternalTasks } from "../hooks/useClinic";
import apiService from "../services/api";
import { useTablePreferences } from "../hooks/useTablePreferences";
import { useListViews } from "../hooks/useListViews";
import { CustomButton } from "../components/ui/CustomButton";
import { User, GetInternalTasksPayload, ListView, ListViewData, ClinicUserApiResponse, InternalTaskItem, UpdateInternalTaskResponse } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ManageColumnsModal, ColumnConfig } from "../components/modals/ManageColumnsModal";
import { SaveViewModal } from "../components/modals/SaveViewModal";
import { ViewSelector } from "../components/ui/ViewSelector";
import { UpdateInternalTaskModal } from "../components/modals/UpdateInternalTaskModal";
import { EditPatientModal } from "../components/modals/EditPatientModal";
import { NewMeetingModal } from "../components/modals/NewMeetingModal";
import { PSYSummaryModal } from "../components/modals/PSYSummaryModal";
import { CMSummaryModal } from "../components/modals/CMSummaryModal";
import { saveLastViewId, getLastViewId, clearLastViewId } from "../utils/viewPersistence";

interface InternalTasksScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users", patientId?: string | number, meetingId?: string | number) => void;
    users: ClinicUserApiResponse[];
    meetingTypes?: any[]; // Optional - will fetch if not provided
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

export const InternalTasksScreen: React.FC<InternalTasksScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    users,
    meetingTypes: propsMeetingTypes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Check if user is admin first (needed for state initialization)
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    // Calculate default "only mine" value based on user role and type
    const defaultOnlyMy = useMemo(() => {
        const isAdminRole = user?.role === 'admin' || user?.role === 'Admin';
        const isCaseManagerOrDoctor = user.type === 'doctor' || user.type === 'case_manager';
        return isAdminRole && isCaseManagerOrDoctor;
    }, [user?.role, user.type]);

    const [selectedClinicId, setSelectedClinicId] = useState<number>(0);
    const [search, setSearch] = useState("");

    // Filter states
    const [selectedUser, setSelectedUser] = useState<number | "">("");
    const [selectedStatus, setSelectedStatus] = useState<string>("Open");
    const [selectedPriority, setSelectedPriority] = useState<string>("");
    const [selectedTaskType, setSelectedTaskType] = useState<string>("");
    const [onlyMy, setOnlyMy] = useState<boolean>(defaultOnlyMy);
    const [selectedActionFilter, setSelectedActionFilter] = useState<string>("Need Action");

    // Define all available columns
    const ALL_COLUMNS: ColumnConfig[] = [
        { id: "id", label: "ID", visible: false },
        { id: "type", label: "Task Type", visible: true },
        { id: "priority", label: "Priority", visible: true },
        { id: "status", label: "Status", visible: true },
        { id: "description", label: "Description", visible: true },
        { id: "HM_ID", label: "HM ID", visible: true },
        { id: "first_name", label: "First name", visible: true },
        { id: "last_name", label: "Last name", visible: true },
        { id: "doctor_id", label: "Doctor", visible: false },
        { id: "case_manager_id", label: "Case Manager", visible: false },
        { id: "created_at", label: "Created", visible: true },
        { id: "last_update", label: "Last Update", visible: false },
        { id: "created_by", label: "Created By", visible: false },
        { id: "assign_to", label: "Assigned To", visible: true },
        { id: "patient_id", label: "Patient ID", visible: false },
        { id: "clinic_id", label: "Clinic", visible: false },
        { id: "meeting_id", label: "Meeting ID", visible: false },
    ];

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "created_at",
        sortOrder: "desc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'internal-tasks-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    const token = localStorage.getItem("token") || "";

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);
    const [baseViewId, setBaseViewId] = useState<number | null>(null);
    const [isUpdateTaskModalOpen, setIsUpdateTaskModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<InternalTaskItem | null>(null);


    // Modal states for action buttons
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [isNewMeetingModalOpen, setIsNewMeetingModalOpen] = useState(false);
    const [isPSYSummaryModalOpen, setIsPSYSummaryModalOpen] = useState(false);
    const [isCMSummaryModalOpen, setIsCMSummaryModalOpen] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [patientDataForEdit, setPatientDataForEdit] = useState<any>(null);
    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [meetingTypes, setMeetingTypes] = useState<any[]>(propsMeetingTypes || []);
    const [taskTypes, setTaskTypes] = useState<any[]>([]);

    // List Views integration
    const {
        privateViews,
        publicViews,
        defaultView,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
        isCreating,
    } = useListViews({
        userId: user?.id as number,
        listName: 'internal-tasks',
        token,
        enabled: !!user?.id && !!token,
    });

    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    React.useEffect(() => {
        if (isLoadingViews) return;
        if (hasLoadedInitialView.current) return;

        const savedViewId = getLastViewId('internal-tasks');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                clearLastViewId('internal-tasks');
            }
        } else if (defaultView) {
            handleLoadView(defaultView);
            hasLoadedInitialView.current = true;
        } else {
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews, defaultView]);

    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    // Sync preferences when state changes
    React.useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    // Use clinics from props
    const clinics = useMemo(
        () => propsClinics?.items || [],
        [propsClinics?.items]
    );

    // Use passed-in users instead of fetching separately
    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [users]);

    const getUserName = React.useCallback((userId: number): string => {
        return userNameMap[userId] || "";
    }, [userNameMap]);

    // Create clinic name map
    const clinicNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        clinics.forEach((clinic: any) => {
            map[clinic.id] = clinic.name;
        });
        return map;
    }, [clinics]);

    const getClinicName = React.useCallback((clinicId: number): string => {
        return clinicNameMap[clinicId] || "";
    }, [clinicNameMap]);

    // Update meeting types when prop changes
    useEffect(() => {
        if (propsMeetingTypes) {
            setMeetingTypes(propsMeetingTypes);
        }
    }, [propsMeetingTypes]);

    // Fetch meeting types and task types on mount (only if not provided via props)
    // Use a ref to prevent duplicate fetches (handles React Strict Mode double-mounting)
    const hasFetchedRef = React.useRef(false);
    const isFetchingRef = React.useRef(false);

    useEffect(() => {
        // Skip if currently fetching or already fetched
        if (isFetchingRef.current || hasFetchedRef.current) return;
        if (!token) return;

        isFetchingRef.current = true;

        const fetchTypes = async () => {
            try {
                const promises: Promise<any>[] = [];

                // Only fetch meeting types if not provided via props
                const shouldFetchMeetingTypes = !propsMeetingTypes || propsMeetingTypes.length === 0;
                if (shouldFetchMeetingTypes) {
                    promises.push(apiService.getMeetingTypes(token, false));
                }

                // Fetch task types
                promises.push(apiService.getTaskTypes(token));

                if (promises.length === 0) {
                    isFetchingRef.current = false;
                    return;
                }

                const results = await Promise.all(promises);

                let resultIndex = 0;
                if (shouldFetchMeetingTypes) {
                    setMeetingTypes(results[resultIndex]);
                    resultIndex++;
                }
                setTaskTypes(results[resultIndex]);
                hasFetchedRef.current = true;
            } catch (error) {
                console.error('Failed to fetch types:', error);
            } finally {
                isFetchingRef.current = false;
            }
        };

        fetchTypes();
    }, [token, propsMeetingTypes]);

    const tasksPayload: GetInternalTasksPayload = useMemo(
        () => {
            const payload: GetInternalTasksPayload = {
                token,
                patient_id: 0,
                user_id: 0,
                clinic_id: selectedClinicId,
            };

            return payload;
        },
        [token, selectedClinicId]
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const tasksQuery = useInternalTasks(tasksPayload);
    const queryClient = useQueryClient();

    // Apply a task the server just saved to the cached list (and to the row the
    // open modal is holding) so the change shows immediately, instead of only
    // once the refetch lands. Merged, not replaced: the list endpoint joins in
    // fields - case manager, doctor - that the task record itself doesn't carry.
    const applySavedTask = React.useCallback((updatedTask?: UpdateInternalTaskResponse) => {
        if (!updatedTask) return;
        queryClient.setQueryData<InternalTaskItem[]>(
            ["internalTasks", tasksPayload.clinic_id, tasksPayload.patient_id, tasksPayload.user_id],
            (previous) => previous?.map(t => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t))
        );
        setSelectedTask(previous => (
            previous && previous.id === updatedTask.id ? { ...previous, ...updatedTask } : previous
        ));
    }, [queryClient, tasksPayload.clinic_id, tasksPayload.patient_id, tasksPayload.user_id]);

    // Extract unique filter options from tasks
    const filterOptions = useMemo(() => {
        const tasks = tasksQuery.data || [];

        const usersSet = new Set<number>();
        const statusesSet = new Set<string>();
        const prioritiesSet = new Set<string>();
        const taskTypesSet = new Set<string>();

        tasks.forEach(task => {
            // Add created_by user
            if (task.created_by) usersSet.add(task.created_by);
            // Add all assigned users
            if (task.assign_to && Array.isArray(task.assign_to)) {
                task.assign_to.forEach(userId => usersSet.add(userId));
            }
            if (task.status) statusesSet.add(task.status);
            if (task.priority) prioritiesSet.add(task.priority);
            if (task.type) taskTypesSet.add(task.type);
        });

        return {
            users: Array.from(usersSet)
                .filter(userId => {
                    const name = getUserName(userId);
                    return name && name.trim() !== '';
                })
                .sort((a, b) => {
                    const nameA = getUserName(a)?.toLowerCase() || '';
                    const nameB = getUserName(b)?.toLowerCase() || '';
                    return nameA.localeCompare(nameB);
                }),
            statuses: Array.from(statusesSet).sort(),
            priorities: Array.from(prioritiesSet).sort(),
            taskTypes: Array.from(taskTypesSet).sort()
        };
    }, [tasksQuery.data, getUserName]);

    // Filter and sort tasks
    const filteredAndSortedTasks = useMemo(() => {
        let tasks = tasksQuery.data || [];
        const currentUserId = typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10);

        // Determine which user to filter by based on "only mine" checkbox or selected user
        // For non-admins, always filter by the current user
        let filterByUserId: number | null = null;
        if (!isAdmin) {
            // Non-admins always filter by their own tasks
            filterByUserId = currentUserId;
        } else if (onlyMy) {
            filterByUserId = currentUserId;
        } else if (selectedUser !== "") {
            filterByUserId = selectedUser;
        }

        // Apply action filter only if we have a user to filter by
        if (filterByUserId !== null) {
            if (selectedActionFilter === "Need Action") {
                tasks = tasks.filter(task =>
                    (task.assign_to.includes(filterByUserId!) && task.status !== "Done" && task.status !== "Waiting for Response" && task.status !== "Finish") ||
                    (task.created_by === filterByUserId! && (task.status === "Waiting for Response" || task.status === "Finish"))
                );
            } else if (selectedActionFilter === "Assign To") {
                tasks = tasks.filter(task => task.assign_to.includes(filterByUserId!));
            } else if (selectedActionFilter === "Created By") {
                tasks = tasks.filter(task => task.created_by === filterByUserId!);
            } else if (selectedActionFilter === "All") {
                // "All" - show all tasks where user is either assigned to OR created by
                tasks = tasks.filter(task =>
                    task.assign_to.includes(filterByUserId!) || task.created_by === filterByUserId!
                );
            }
        }

        // Filter by status
        if (selectedStatus) {
            if (selectedStatus === "Open") {
                // "Open" encompasses New, In Progress, Waiting for Response, and Finish
                tasks = tasks.filter(task =>
                    task.status === "New" ||
                    task.status === "In Progress" ||
                    task.status === "Waiting for Response" ||
                    task.status === "Finish"
                );
            } else {
                tasks = tasks.filter(task => task.status === selectedStatus);
            }
        }

        // Filter by priority
        if (selectedPriority) {
            tasks = tasks.filter(task => task.priority === selectedPriority);
        }

        // Filter by task type
        if (selectedTaskType) {
            tasks = tasks.filter(task => task.type === selectedTaskType);
        }

        // Filter by search
        if (search) {
            const searchLower = search.toLowerCase();
            tasks = tasks.filter(task => {
                return (
                    task.first_name?.toLowerCase().includes(searchLower) ||
                    task.last_name?.toLowerCase().includes(searchLower) ||
                    task.HM_ID?.toLowerCase().includes(searchLower) ||
                    task.type?.toLowerCase().includes(searchLower) ||
                    task.description?.toLowerCase().includes(searchLower) ||
                    task.status?.toLowerCase().includes(searchLower) ||
                    task.priority?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Sort tasks
        // Doctor / Case Manager / Created By columns hold numeric user ids but display
        // names, so sort them by the resolved name rather than the raw id.
        const nameColumns = ["doctor_id", "case_manager_id", "created_by"];
        tasks.sort((a, b) => {
            let aValue: any = (a as any)[sortColumn];
            let bValue: any = (b as any)[sortColumn];

            if (nameColumns.includes(sortColumn)) {
                aValue = aValue ? getUserName(aValue).toLowerCase() : "";
                bValue = bValue ? getUserName(bValue).toLowerCase() : "";
            }

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined || aValue === "") return 1;
            if (bValue === null || bValue === undefined || bValue === "") return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortOrder === "asc" ? comparison : -comparison;
        });

        return tasks;
    }, [tasksQuery.data, selectedUser, selectedStatus, selectedPriority, selectedTaskType, search, sortColumn, sortOrder, onlyMy, user.id, selectedActionFilter, isAdmin, getUserName]);

    const handleRefresh = () => {
        tasksQuery.refetch();
    };

    const handleClearFilters = () => {
        setSelectedUser("");
        setSelectedStatus("Open");
        setSelectedPriority("");
        setSelectedTaskType("");
        setOnlyMy(false);
        setSelectedActionFilter("Need Action");
    };

    // Format date
    const formatDate = (timestamp: number | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' });
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
    };

    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);

    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't sort if clicking on resize handle
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-gray-100 cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-blue-600">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        setSearch("");
        setSelectedClinicId(0);
        setSelectedUser("");
        setSelectedStatus("Open");
        setSelectedPriority("");
        setSelectedTaskType("");
        setSelectedActionFilter("Need Action");
        setOnlyMy(defaultOnlyMy);

        // Clear saved view
        setCurrentViewId(null);
        clearLastViewId('internal-tasks');

        resetToDefaults();
    };

    // View management handlers
    const handleUpdateView = async () => {
        if (!baseViewId) return;

        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            filters: {
                search,
                selectedClinicId,
                selectedUser,
                selectedStatus,
                selectedPriority,
                selectedTaskType,
                selectedActionFilter,
                onlyMy,
            },
        };

        try {
            await updateListView({
                id: baseViewId,
                data: viewData,
            });
            setCurrentViewId(baseViewId);
            saveLastViewId('internal-tasks', baseViewId);
            hasLoadedInitialView.current = true;
        } catch (error) {
            console.error('Failed to update view:', error);
        }
    };

    const handleSaveAsView = async (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => {
        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            description,
            filters: {
                search,
                selectedClinicId,
                selectedUser,
                selectedStatus,
                selectedPriority,
                selectedTaskType,
                selectedActionFilter,
                onlyMy,
            },
            ...(isDefault && { is_default: true }),
        };

        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'internal-tasks',
                data: viewData,
            });
            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('internal-tasks', newView.id);
            hasLoadedInitialView.current = true;
            setIsSaveViewModalOpen(false);
        } catch (error) {
            console.error('Failed to save view:', error);
        }
    };

    const handleLoadView = (view: ListView) => {
        setCurrentViewId(view.id);
        setBaseViewId(view.id);
        // Merge in any columns added since this view was saved (e.g. doctor/case_manager),
        // so they still appear in Manage Columns for users with older saved views.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter(id => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);

        // Check if clinic will change
        const newClinicId = view.data.filters?.selectedClinicId ?? 0;
        const clinicChanged = newClinicId !== selectedClinicId;

        // Always apply filters from the view (or clear if not defined)
        if (view.data.filters) {
            setSearch(view.data.filters.search ?? "");
            setSelectedClinicId(newClinicId);

            // Handle the mutual exclusivity between onlyMy and selectedUser
            const viewOnlyMy = view.data.filters.onlyMy ?? defaultOnlyMy;
            const viewSelectedUser = view.data.filters.selectedUser ?? "";

            if (viewOnlyMy && viewSelectedUser !== "") {
                // If both are set in the view, prioritize onlyMy and clear selectedUser
                setOnlyMy(viewOnlyMy);
                setSelectedUser("");
            } else {
                setOnlyMy(viewOnlyMy);
                setSelectedUser(viewSelectedUser);
            }

            setSelectedStatus(view.data.filters.selectedStatus ?? "Open");
            setSelectedPriority(view.data.filters.selectedPriority ?? "");
            setSelectedTaskType(view.data.filters.selectedTaskType ?? "");
            setSelectedActionFilter(view.data.filters.selectedActionFilter ?? "Need Action");
        } else {
            // Clear filters if the view doesn't have any defined
            setSearch("");
            setSelectedClinicId(0);
            setSelectedUser("");
            setSelectedStatus("Open");
            setSelectedPriority("");
            setSelectedTaskType("");
            setSelectedActionFilter("Need Action");
            setOnlyMy(defaultOnlyMy);
        }

        setCurrentViewId(view.id);
        saveLastViewId('internal-tasks', view.id);

        // Refetch data if clinic changed
        if (clinicChanged) {
            // Use setTimeout to ensure state updates are processed first
            setTimeout(() => {
                tasksQuery.refetch();
            }, 0);
        }
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                handleResetColumns();
                setCurrentViewId(null);
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    const renderTasksTab = () => {
        if (tasksQuery.isLoading) {
            return (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading internal tasks...</p>
                </div>
            );
        }

        if (tasksQuery.isError) {
            return (
                <div className="text-center py-12 text-red-600">
                    <p>Error loading internal tasks. Please try again.</p>
                    <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        className="mt-4"
                    >
                        Retry
                    </CustomButton>
                </div>
            );
        }

        const tasks = filteredAndSortedTasks;

        return (
            <div className="space-y-4">
                {tasks.length === 0 ? (
                    <div className="text-center py-12 text-gray-600">
                        <p>No internal tasks found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                        const columnConfig: Record<string, { label: string; nonSortable?: boolean }> = {
                                            "id": { label: "ID" },
                                            "type": { label: "Task Type" },
                                            "priority": { label: "Priority" },
                                            "status": { label: "Status" },
                                            "description": { label: "Description", nonSortable: true },
                                            "HM_ID": { label: "HM ID" },
                                            "first_name": { label: "First name" },
                                            "last_name": { label: "Last name" },
                                            "doctor_id": { label: "Doctor" },
                                            "case_manager_id": { label: "Case Manager" },
                                            "created_at": { label: "Created" },
                                            "last_update": { label: "Last Update" },
                                            "created_by": { label: "Created By" },
                                            "assign_to": { label: "Assigned To", nonSortable: true },
                                            "patient_id": { label: "Patient ID" },
                                            "clinic_id": { label: "Clinic" },
                                            "meeting_id": { label: "Meeting ID" },
                                        };

                                        const config = columnConfig[columnId];
                                        return (
                                            <ResizableHeader
                                                key={columnId}
                                                column={columnId}
                                                label={config.label}
                                                nonSortable={config.nonSortable}
                                            />
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tasks.map((task: InternalTaskItem) => {
                                    const createdByName = getUserName(task.created_by);
                                    const assignedToNames = task.assign_to.map(id => getUserName(id)).filter(name => name).join(", ");
                                    const doctorName = task.doctor_id ? getUserName(task.doctor_id) : "";
                                    const caseManagerName = task.case_manager_id ? getUserName(task.case_manager_id) : "";

                                    const getCellContent = (columnId: string) => {
                                        switch (columnId) {
                                            case "id":
                                                return <span className="text-gray-900">{task.id}</span>;
                                            case "type":
                                                return (
                                                    <span
                                                        className="text-blue-600 hover:underline cursor-pointer"
                                                        onClick={() => {
                                                            setSelectedTask(task);
                                                            setIsUpdateTaskModalOpen(true);
                                                        }}
                                                    >
                                                        {task.type || "-"}
                                                    </span>
                                                );
                                            case "priority":
                                                const priorityColors: Record<string, string> = {
                                                    "Urgent": "text-red-600 font-semibold",
                                                    "High": "text-orange-600",
                                                    "Medium": "text-yellow-600",
                                                    "Low": "text-green-600",
                                                };
                                                return (
                                                    <span className={priorityColors[task.priority] || "text-gray-900"}>
                                                        {task.priority || "-"}
                                                    </span>
                                                );
                                            case "status":
                                                return <span className="text-gray-900">{task.status || "-"}</span>;
                                            case "description":
                                                return (
                                                    <span className="text-gray-900 whitespace-pre-wrap">
                                                        {task.description || "-"}
                                                    </span>
                                                );
                                            case "HM_ID":
                                                return (
                                                    <span
                                                        className="text-blue-600 hover:underline cursor-pointer"
                                                        onClick={() => {
                                                            if (task.patient_id && onNavigate) {
                                                                onNavigate("patient", task.patient_id);
                                                            }
                                                        }}
                                                    >
                                                        {task.HM_ID || "-"}
                                                    </span>
                                                );
                                            case "first_name":
                                                return task.first_name ? (
                                                    <span
                                                        className="text-blue-600 hover:underline cursor-pointer"
                                                        onClick={async () => {
                                                            if (!task.patient_id) return;

                                                            setIsLoadingPatientData(true);

                                                            // Small delay to ensure spinner is visible
                                                            await new Promise(resolve => setTimeout(resolve, 100));

                                                            try {
                                                                const patientData = await apiService.getPatientPage({
                                                                    token,
                                                                    patient_id: task.patient_id
                                                                });

                                                                setSelectedTask(task);
                                                                setPatientDataForEdit(patientData);
                                                                setIsEditPatientModalOpen(true);
                                                            } catch (error) {
                                                                console.error('Failed to fetch patient data:', error);
                                                            } finally {
                                                                setIsLoadingPatientData(false);
                                                            }
                                                        }}
                                                    >
                                                        {task.first_name}
                                                    </span>
                                                ) : <span className="text-gray-900">-</span>;
                                            case "last_name":
                                                return <span className="text-gray-900">{task.last_name || "-"}</span>;
                                            case "doctor_id":
                                                return <span className="text-gray-900">{doctorName || "-"}</span>;
                                            case "case_manager_id":
                                                return <span className="text-gray-900">{caseManagerName || "-"}</span>;
                                            case "created_at":
                                                return <span className="text-gray-900">{formatDate(task.created_at)}</span>;
                                            case "last_update":
                                                return <span className="text-gray-900">{formatDate(task.last_update)}</span>;
                                            case "created_by":
                                                return <span className="text-gray-900">{createdByName || "-"}</span>;
                                            case "assign_to":
                                                return <span className="text-gray-900">{assignedToNames || "-"}</span>;
                                            case "patient_id":
                                                return <span className="text-gray-900">{task.patient_id || "-"}</span>;
                                            case "clinic_id":
                                                const clinicName = getClinicName(task.clinic_id);
                                                return <span className="text-gray-900">{clinicName || task.clinic_id || "-"}</span>;
                                            case "meeting_id":
                                                return <span className="text-gray-900">{task.meeting_id || "-"}</span>;
                                            default:
                                                return <span className="text-gray-900">-</span>;
                                        }
                                    };

                                    return (
                                        <tr key={`${task.id}-${task.type}`} className="hover:bg-gray-50">
                                            {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => (
                                                <td key={columnId} className="px-4 py-3 text-sm whitespace-nowrap">
                                                    {getCellContent(columnId)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="internal-tasks"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            {/* Internal Tasks Section */}
            <div className="bg-white rounded-lg shadow mb-8">
                <div className="border-b border-gray-200 px-6 py-4">
                    {/* Header row with title and view selector */}
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Internal Tasks ({filteredAndSortedTasks.length})</h2>
                            <ViewSelector
                                privateViews={privateViews}
                                publicViews={publicViews}
                                currentViewId={currentViewId}
                                baseViewId={baseViewId}
                                onSelectView={handleLoadView}
                                onSaveView={handleUpdateView}
                                onSaveAsView={() => setIsSaveViewModalOpen(true)}
                                onDeleteView={handleDeleteView}
                                isLoading={isLoadingViews}
                                isAdmin={isAdmin}
                            />
                        </div>
                        <CustomButton
                            variant="secondary"
                            size="sm"
                            onClick={handleManageColumns}
                        >
                            ⚙️ Manage Columns
                        </CustomButton>
                    </div>

                    {/* Controls section */}
                    <div className="space-y-4">
                        {/* First row: Search and Clinic */}
                        <div className="flex gap-4 items-center">
                            <div className="flex-1">
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="Search by name, HM ID, type, or description..."
                                    value={search}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="w-64">
                                <select
                                    id="clinic-select"
                                    value={selectedClinicId}
                                    onChange={(e) => setSelectedClinicId(Number(e.target.value))}
                                    disabled={false}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                    <option value={0}>All Clinics</option>
                                    {clinics.map((clinic: any) => (
                                        <option key={clinic.id} value={clinic.id}>
                                            {clinic.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Second row: Filters */}
                        <div className="flex gap-4 items-center">
                            {/* User filter - only show for admins */}
                            {isAdmin && (
                                <div className="flex-1">
                                    <select
                                        id="user-filter"
                                        value={selectedUser}
                                        onChange={(e) => {
                                            const newSelectedUser = e.target.value === "" ? "" : Number(e.target.value);
                                            setSelectedUser(newSelectedUser);
                                            if (newSelectedUser !== "" && onlyMy) {
                                                setOnlyMy(false);
                                            }
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Users</option>
                                        {filterOptions.users.map((userId) => (
                                            <option key={userId} value={userId}>
                                                {getUserName(userId)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Only mine checkbox - only show for admins */}
                            {isAdmin && (
                                <label className="w-auto flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={onlyMy}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setOnlyMy(checked);
                                            if (checked && selectedUser !== "") {
                                                setSelectedUser("");
                                            }
                                        }}
                                        className="cursor-pointer"
                                    />
                                    <span className="text-sm text-gray-600">Only mine</span>
                                </label>
                            )}

                            {/* Action filter dropdown - always show for non-admins, show for admins when filtering by a user */}
                            {(!isAdmin || onlyMy || selectedUser !== "") && (
                                <div className="w-48">
                                    <select
                                        id="action-filter"
                                        value={selectedActionFilter}
                                        onChange={(e) => setSelectedActionFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="Need Action">Need Action</option>
                                        <option value="Assign To">Assign To</option>
                                        <option value="Created By">Created By</option>
                                        <option value="All">All</option>
                                    </select>
                                </div>
                            )}

                            <div className="flex-1">
                                <select
                                    id="status-filter"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Open">Open</option>
                                    <option value="New">New</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Waiting for Response">Waiting for Response</option>
                                    <option value="Finish">Finish</option>
                                    <option value="Done">Done</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="priority-filter"
                                    value={selectedPriority}
                                    onChange={(e) => setSelectedPriority(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Priorities</option>
                                    {filterOptions.priorities.map((priority) => (
                                        <option key={priority} value={priority}>
                                            {priority}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="task-type-filter"
                                    value={selectedTaskType}
                                    onChange={(e) => setSelectedTaskType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Task Types</option>
                                    {filterOptions.taskTypes.map((taskType) => (
                                        <option key={taskType} value={taskType}>
                                            {taskType}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <CustomButton
                                variant="secondary"
                                size="sm"
                                onClick={handleClearFilters}
                            >
                                Clear Filters
                            </CustomButton>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderTasksTab()}
                </div>
            </div>

            {/* Manage Columns Modal */}
            <ManageColumnsModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={columnOrder.map(id => ({
                    id,
                    label: ALL_COLUMNS.find(col => col.id === id)?.label || id,
                    visible: columnVisibility[id] ?? true,
                }))}
                onSave={handleSaveColumns}
                onReset={handleResetColumns}
            />

            {/* Save As View Modal */}
            <SaveViewModal
                isOpen={isSaveViewModalOpen}
                onClose={() => setIsSaveViewModalOpen(false)}
                onSave={handleSaveAsView}
                currentConfig={{
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                }}
                isAdmin={isAdmin}
                isSaving={isCreating}
                existingDefaultView={defaultView}
            />

            {/* Update Task Modal */}
            {selectedTask && (
                <UpdateInternalTaskModal
                    isOpen={isUpdateTaskModalOpen}
                    onClose={() => {
                        setIsUpdateTaskModalOpen(false);
                        setSelectedTask(null);
                    }}
                    task={selectedTask}
                    users={users}
                    taskTypes={taskTypes}
                    meetingTypes={meetingTypes}
                    currentUserId={typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10)}
                    currentUserType={user.type}
                    isAdmin={isAdmin}
                    onSuccess={(updatedTask) => {
                        applySavedTask(updatedTask);
                        tasksQuery.refetch();
                    }}
                    onNavigate={onNavigate}
                    onOpenEditPatient={() => {
                        // Fetch patient data then open modal
                        const fetchPatientData = async () => {
                            if (!selectedTask?.patient_id) return;

                            setIsLoadingPatientData(true);
                            try {
                                const patientData = await apiService.getPatientPage({
                                    token,
                                    patient_id: selectedTask.patient_id
                                });
                                setPatientDataForEdit(patientData);
                                setIsEditPatientModalOpen(true);
                            } catch (error) {
                                console.error('Failed to fetch patient data:', error);
                            } finally {
                                setIsLoadingPatientData(false);
                            }
                        };
                        fetchPatientData();
                    }}
                    onOpenNewMeeting={() => {
                        setIsNewMeetingModalOpen(true);
                    }}
                    onOpenPSYSummary={(meetingId: number) => {
                        setSelectedMeetingId(meetingId);
                        setIsPSYSummaryModalOpen(true);
                    }}
                    onOpenCMSummary={(meetingId: number) => {
                        setSelectedMeetingId(meetingId);
                        setIsCMSummaryModalOpen(true);
                    }}
                />
            )}

            {/* Edit Patient Modal */}
            {selectedTask && isEditPatientModalOpen && patientDataForEdit && (
                <EditPatientModal
                    isOpen={isEditPatientModalOpen}
                    onClose={() => {
                        setIsEditPatientModalOpen(false);
                        setPatientDataForEdit(null);
                    }}
                    patient={patientDataForEdit}
                    onSave={async (updatedData: any) => {
                        await apiService.updatePatientInfo(token, selectedTask.patient_id, updatedData);
                        setIsEditPatientModalOpen(false);
                        setPatientDataForEdit(null);
                        tasksQuery.refetch();
                    }}
                />
            )}

            {/* New Meeting Modal */}
            {selectedTask && isNewMeetingModalOpen && (
                <NewMeetingModal
                    isOpen={isNewMeetingModalOpen}
                    onClose={() => setIsNewMeetingModalOpen(false)}
                    patient={{
                        id: selectedTask.patient_id,
                        HM_ID: selectedTask.HM_ID,
                        first_name: selectedTask.first_name,
                        last_name: selectedTask.last_name,
                        doctor_id: selectedTask.doctor_id,
                        case_manager_id: selectedTask.case_manager_id,
                        default_doctor_id: selectedTask.default_doctor_id,
                        default_case_manager_id: selectedTask.default_case_manager_id
                    }}
                    users={users}
                    meetingTypes={meetingTypes}
                    currentUser={user}
                />
            )}

            {/* PSY Summary Modal */}
            {selectedMeetingId && isPSYSummaryModalOpen && (
                <PSYSummaryModal
                    isOpen={isPSYSummaryModalOpen}
                    onClose={() => {
                        setIsPSYSummaryModalOpen(false);
                        setSelectedMeetingId(null);
                    }}
                    meetingId={selectedMeetingId}
                    clinicId={selectedTask?.clinic_id}
                    token={token}
                    medicationTypes={[]} // TODO: Pass medication types if available
                    diagnosisCodes={[]} // TODO: Pass diagnosis codes if available
                    onSuccess={() => {
                        setIsPSYSummaryModalOpen(false);
                        setSelectedMeetingId(null);
                        tasksQuery.refetch();
                    }}
                />
            )}

            {/* CM Summary Modal */}
            {selectedMeetingId && isCMSummaryModalOpen && (
                <CMSummaryModal
                    isOpen={isCMSummaryModalOpen}
                    onClose={() => {
                        setIsCMSummaryModalOpen(false);
                        setSelectedMeetingId(null);
                    }}
                    meetingId={selectedMeetingId}
                    token={token}
                    onSuccess={() => {
                        setIsCMSummaryModalOpen(false);
                        setSelectedMeetingId(null);
                        tasksQuery.refetch();
                    }}
                />
            )}

            {/* Loading Overlay */}
            {isLoadingPatientData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading patient data...</p>
                    </div>
                </div>
            )}
        </Layout>
    );
};
